import { useCallback, useContext, useEffect, useState } from "react";
import { Page } from "pages/Page";
import { GlobalContext } from "contexts/GlobalContext";
import { GetGameStateResult } from "results/frankenstories/GetGameStateResult";
import { CompletedStory } from "components/game/CompletedStory";
import { RouteLink } from "Routes";
import { CompletedGamePlayerStateResult } from "results/frankenstories/CompletedGamePlayerStateResult";
import { CodeProps } from "types/CodeProps";
import { CheckInModal } from "components/modals/CheckInModal";
import { useHistory } from "react-router-dom";

const GamesStoryPage = (props: CodeProps) => {
    const { code } = props;

    const history = useHistory();

    const { gamesService, gameplayService, setError, catchServerError } = useContext(GlobalContext);
    const { getCompletedPlayerState } = gamesService;
    const { getGameState } = gameplayService;

    const [loading, setLoading] = useState(true);
    const [gameState, setGameState] = useState<GetGameStateResult>();
    const [playerState, setPlayerState] = useState<CompletedGamePlayerStateResult>();
    const [showCheckIn, setShowCheckIn] = useState(false);

    const loadPlayerState = useCallback(() => {
        if (code) {
            getCompletedPlayerState(code)
                .then(x => {
                    setPlayerState(x);
                })
                .catch(catchServerError);
        }
    }, [code, getCompletedPlayerState, catchServerError]);

    // get the story
    useEffect(() => {
        let mounted = true;

        if (code) {
            getGameState(code)
                .then(x => {
                    if (mounted) {
                        setGameState(x);
                    }
                })
                .catch(() => {
                    setError("Could not find game");
                });
        }

        return () => {
            mounted = false;
        };
    }, [code, getGameState, setError]);

    // initial load of the player data
    useEffect(() => {
        loadPlayerState();
    }, [loadPlayerState]);

    // update loading when both game state and player state is loaded
    useEffect(() => {
        if (loading && gameState && playerState) {
            setLoading(false);
        }
    }, [gameState, loading, playerState]);

    // show check in
    useEffect(() => {
        if (localStorage.getItem("FlagCheckIn")) {
            // remove flag
            localStorage.removeItem("FlagCheckIn");

            // show modal
            setShowCheckIn(true);
        }
    }, []);

    // determine if in hall of fame
    const { pathname } = location;

    const completedGameUrl = RouteLink.gamesCompletedGame(code);
    const hallOfFameUrl = RouteLink.discoverHallOfFameStory(code);

    const isHallOfFame = pathname === hallOfFameUrl;

    // manage title
    const title = gameState?.title ?? gameState?.gameName ?? "...";

    useEffect(() => {
        if (gameState != null) {
            // non hall of fame games must get redirected to my stories
            if (!gameState?.isHallOfFameApproved) {
                // check url
                const correctUrl = completedGameUrl;

                if (pathname !== correctUrl) {
                    history.push(correctUrl);
                }
            }
        }
    }, [completedGameUrl, gameState, history, pathname]);

    const description = gameState?.story.at(0)?.body ?? "";

    // trim the description to 160 characters and keep whole words
    const trimmedDescription = description.length > 157 ? description.substring(0, 157) : description;
    const trimmedDescriptionWithEllipsis =
        trimmedDescription.length >= 157 ? `${trimmedDescription}...` : trimmedDescription;

    return (
        <Page
            title={title}
            description={trimmedDescriptionWithEllipsis}
            imageUrl={gameState?.imageUrl}
            canonical={location.pathname}
            authType="Public"
        >
            <div className="vstack gap-4 gap-lg-5">
                <CompletedStory
                    gameState={gameState}
                    playerState={playerState}
                    code={code}
                    isHallOfFame={isHallOfFame}
                />
            </div>

            {showCheckIn && <CheckInModal />}
        </Page>
    );
};

export default GamesStoryPage;
